/**
 * Whether a failed POI lookup failed because an admin removed the POI, and why.
 *
 * The backend answers `poi(type:, id:)` for a removed POI with a GraphQL error
 * whose `extensions.code` is POI_DELETED (Queries::PoiDeletedError) — the map
 * tiles still carried it when this page loaded — and `extensions.reason` is the
 * admin's reason (Poiable::DELETION_REASONS). Matched on the codes, never the
 * message, so the server-side copy can change without breaking this.
 *
 * Takes `unknown` because it is handed whatever a `catch` caught: graphql-request
 * throws a ClientError carrying `response.errors`, but a network failure, an
 * abort or a plain Error lands in the same catch and must read as "not removed".
 */
export const POI_DELETED_CODE = 'POI_DELETED';

type GraphQLErrorEntry = { extensions?: { code?: unknown; reason?: unknown } } | null;

function deletedEntry(error: unknown): GraphQLErrorEntry | undefined {
  const errors = (error as { response?: { errors?: unknown } } | null)?.response?.errors;
  if (!Array.isArray(errors)) return undefined;

  return errors.find((entry) => (entry as GraphQLErrorEntry)?.extensions?.code === POI_DELETED_CODE);
}

export function isPoiDeletedError(error: unknown): boolean {
  return deletedEntry(error) !== undefined;
}

/** The admin's reason, or null when none was given (or the POI was not removed). */
export function poiDeletedReason(error: unknown): string | null {
  const reason = deletedEntry(error)?.extensions?.reason;
  return typeof reason === 'string' ? reason : null;
}

export type Translate = (key: string) => string;

/**
 * What the sheet says for a removed POI, in the reader's language.
 *
 * Every key is a literal `t('…')` call on purpose: the client bundle only ships
 * keys it can find written out in app/javascript, so a key assembled from the
 * reason would arrive in the browser as the key. An unknown or missing reason —
 * `other`, a POI removed before reasons were recorded, a reason added on the
 * server before this page knew it — says only that it was removed.
 */
export function poiRemovedMessage(reason: string | null, t: Translate): string {
  switch (reason) {
    case 'permanently_closed':
      return t('map_data_poi_dialog.map_data_poi_detail_dialog.poi_removed_permanently_closed');
    case 'no_longer_exists':
      return t('map_data_poi_dialog.map_data_poi_detail_dialog.poi_removed_no_longer_exists');
    case 'duplicate':
      return t('map_data_poi_dialog.map_data_poi_detail_dialog.poi_removed_duplicate');
    case 'no_public_access':
      return t('map_data_poi_dialog.map_data_poi_detail_dialog.poi_removed_no_public_access');
    case 'unsafe':
      return t('map_data_poi_dialog.map_data_poi_detail_dialog.poi_removed_unsafe');
    default:
      return t('map_data_poi_dialog.map_data_poi_detail_dialog.poi_removed');
  }
}
